// Universal date helper functions for safe date handling

/**
 * Safely converts any value to a Date object
 * Always returns a valid Date object, never an invalid date
 */
export const toDate = (value: any): Date => {
  // Return current date for null/undefined
  if (value === null || value === undefined) {
    return new Date();
  }
  
  // If it's already a JavaScript Date
  if (value instanceof Date) {
    // Check if it's a valid date
    if (isNaN(value.getTime())) {
      console.warn('Invalid Date object detected, using current date');
      return new Date();
    }
    return value;
  }
  
  // If it's a Firestore Timestamp (has toDate method)
  if (typeof value?.toDate === 'function') {
    try {
      const converted = value.toDate();
      // Validate the converted date
      if (converted instanceof Date && !isNaN(converted.getTime())) {
        return converted;
      }
      console.warn('Firestore timestamp conversion resulted in invalid date');
      return new Date();
    } catch (error) {
      console.warn('Error converting Firestore timestamp:', error);
      return new Date();
    }
  }
  
  // If it's a timestamp object with seconds (Firestore format)
  if (typeof value?.seconds === 'number') {
    try {
      const date = new Date(value.seconds * 1000);
      if (!isNaN(date.getTime())) {
        return date;
      }
      console.warn('Invalid timestamp seconds value:', value.seconds);
      return new Date();
    } catch (error) {
      console.warn('Error converting timestamp seconds:', error);
      return new Date();
    }
  }
  
  // If it's a string or number, try to parse it
  if (typeof value === 'string' || typeof value === 'number') {
    try {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
      console.warn('Could not parse date from value:', value);
      return new Date();
    } catch (error) {
      console.warn('Error parsing date:', error);
      return new Date();
    }
  }
  
  // For any other type, return current date
  console.warn('Unknown date value type:', typeof value, value);
  return new Date();
};

/**
 * Safely formats a date to locale string
 */
export const formatDate = (
  value: any,
  options?: Intl.DateTimeFormatOptions,
  locale: string = 'en-US'
): string => {
  try {
    const date = toDate(value);
    return date.toLocaleDateString(locale, options);
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Formats date for display with fallback
 */
export const formatDateSafe = (value: any, fallback: string = 'N/A'): string => {
  if (!value) return fallback;
  
  try {
    const date = toDate(value);
    if (isNaN(date.getTime())) return fallback;
    return date.toLocaleDateString();
  } catch {
    return fallback;
  }
};

/**
 * Get ISO date string for form inputs
 */
export const toISODateString = (value: any): string => {
  try {
    const date = toDate(value);
    return date.toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/**
 * Calculate days difference
 */
export const getDaysDifference = (date1: any, date2: any = new Date()): number => {
  try {
    const d1 = toDate(date1);
    const d2 = toDate(date2);
    return Math.ceil((d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
};
